import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Restaurant } from '@/types/restaurant';
import type {
  ExpandedSearchResponse,
  H3Status,
  ProgressiveSearchEnvelope,
} from '@/types/server/restaurantApiResponses';
import { useSearchUpdateSignal, SearchUpdateHint } from '@/lib/ably';
import { getExpandedSearchResults } from './searchRequest';

/**
 * The one ring-consumption implementation in the frontend (D41c CR-8).
 *
 * Both search modes use it: current-location search and location-search,
 * which until now was a bare `useQuery` with no progressive results at all
 * even though the backend had always sent it the metadata.
 *
 * The union is stored in the TanStack Query cache under the owning search's
 * key, so there is exactly one copy of "what the client holds" — the same
 * object the merge reads its `orderedIds` / `orderedIdsVersion` baseline from.
 */

/** Rank-tail trim: with a server-authoritative total order, dropping the tail drops the lowest-ranked. */
export const MAX_RESTAURANTS = 200;

/**
 * D41c Phase 6 (R2-M2) — how long the client waits, from search initiation,
 * before it stops trusting Ably and asks the server directly whether
 * expansion is done. `expansion_complete` on `expanded_results` is the sole
 * completion authority (see `ExpandedSearchResponse`); the Ably `is_final`
 * signal is advisory only — a lost signal must resolve to "complete," never
 * to "loading forever."
 *
 * 120s is PROVISIONAL, not measured: round one's 60s assumed an optimistic
 * clean-fetch path and omitted retries, per-restaurant persistence, and lock
 * contention (see plan § Deadline derivation). The real value is Phase 7's
 * job to set from measured p95 ring timings — this constant is the only
 * place that number needs to change. The re-arm loop below (`reconcile`) is
 * what makes shipping a provisional estimate safe: an under-estimate costs
 * one extra round trip, not a truncated result set.
 *
 * Overridable via `VITE_EXPANSION_DEADLINE_MS` so E2E/manual verification
 * against a non-eager worker doesn't have to wait out the real value.
 */
export const EXPANSION_DEADLINE_MS = Number(import.meta.env.VITE_EXPANSION_DEADLINE_MS) || 120_000;

/**
 * At most two re-arms (three reconciliation attempts total) before a
 * still-running expansion is resolved to "complete" regardless, and the
 * fact is logged. A client that gives up on a live expansion truncates a
 * member's results as surely as one that spins forever suppresses the
 * empty state — this bound exists so neither failure mode is unbounded.
 */
const MAX_DEADLINE_REARMS = 2;

export interface UseRingConsumptionParams {
  /** The active search's identity, or null before the initial search has landed. */
  searchId: string | null;
  /** Whether ring expansion may still have more results to deliver. */
  hasMoreRings: boolean;
  /** The active search's generation token — stale-batch discards compare against this. */
  searchGeneration: string | null;
  /** Query key holding the active search's envelope. Merged batches are written back here. */
  queryKey: readonly unknown[];
  /** The active search's envelope, or undefined before it lands. */
  data: ProgressiveSearchEnvelope | undefined;
  /** Whether the owning search is the active one. A disabled hook consumes nothing. */
  enabled?: boolean;
}

export interface RingConsumptionResult {
  restaurants: Restaurant[];
  hasMoreRings: boolean;
  isRealtimeActive: boolean;
  h3Status: H3Status | null;
}

export type MergeRefusal = 'stale_generation' | 'stale_version' | 'shrunken_order';

export type MergeDecision =
  | { applied: true; envelope: ProgressiveSearchEnvelope }
  | { applied: false; reason: MergeRefusal };

/**
 * Merge one expansion batch into the union the client holds.
 *
 * Consumes D41a's contract as-is: discard on a token that isn't the active
 * search's, then order purely by index lookup into the server's `orderedIds`.
 * There is deliberately no comparison function anywhere in this file (CR-3) —
 * the server owns the order in `bestMatch` mode, and the client's only local
 * sort modes (Nearest, A–Z) live in `Search.tsx` and re-sort the merged list
 * afterwards.
 */
export function mergeRingBatch(
  current: ProgressiveSearchEnvelope,
  batch: ProgressiveSearchEnvelope,
  activeGeneration: string | null,
): MergeDecision {
  // Tokens are instance-unique (they include `search_id`), so a token that
  // differs only in `search_id` is stale too. Comparison happens only when
  // both sides are stamped — an unstamped payload can't be discriminated, and
  // discarding it would strand results rather than protect anything.
  if (activeGeneration && batch.searchGeneration && batch.searchGeneration !== activeGeneration) {
    return { applied: false, reason: 'stale_generation' };
  }

  // `>=` on the version, not `>`: the server's pipelined (members, version)
  // read can legitimately return the same version with more members after a
  // raced write, and refusing that payload would strand results (R2-M4).
  const lastVersion = current.orderedIdsVersion;
  if (
    batch.orderedIdsVersion != null &&
    lastVersion != null &&
    batch.orderedIdsVersion < lastVersion
  ) {
    return { applied: false, reason: 'stale_version' };
  }

  // Compared against the PREVIOUS orderedIds length, not the client's
  // locally-materialized restaurant count — `restaurants.length` legitimately
  // sits below `orderedIds.length` whenever the client holds no data yet for
  // some ordered ids (skipped silently, see below), and permanently once the
  // MAX_RESTAURANTS trim caps it. Either would let a genuinely regressed
  // order slip past a `restaurants.length`-based guard.
  //
  // Strictly `<`, not `<=`: the server can legitimately re-send an
  // EQUAL-length `orderedIds` while a later fetch fills in restaurant DATA
  // for ids the order already named but the client had no row for yet (the
  // full order can be known before every id in it has been materialized).
  // Refusing an equal-length resend would drop that data on the floor —
  // only a batch that's genuinely SHORTER than what was already known is a
  // regression worth refusing.
  const lastOrderedLength = current.orderedIds?.length ?? current.restaurants.length;
  if (batch.orderedIds != null && batch.orderedIds.length < lastOrderedLength) {
    return { applied: false, reason: 'shrunken_order' };
  }

  const byId = new Map<number, Restaurant>();
  for (const restaurant of current.restaurants) byId.set(restaurant.id, restaurant);
  for (const restaurant of batch.restaurants) byId.set(restaurant.id, restaurant);

  const orderedIds = batch.orderedIds ?? current.orderedIds ?? null;

  let ordered: Restaurant[];
  if (orderedIds) {
    ordered = [];
    for (const id of orderedIds) {
      const restaurant = byId.get(id);
      // Ordered ids the client holds no data for are skipped silently: no
      // placeholder, no skeleton, no gap. They are simply the reason a later
      // batch inserts between two visible cards instead of at the end.
      if (restaurant) {
        ordered.push(restaurant);
        byId.delete(id);
      }
    }
    // Ids held locally but absent from `ordered_ids` are appended, never
    // evicted — mirroring the server-side rule.
    for (const restaurant of byId.values()) ordered.push(restaurant);
  } else {
    ordered = Array.from(byId.values());
  }

  return {
    applied: true,
    envelope: {
      ...current,
      restaurants: ordered.slice(0, MAX_RESTAURANTS),
      h3Status: batch.h3Status ?? current.h3Status,
      orderedIds,
      orderedIdsVersion: batch.orderedIdsVersion ?? lastVersion ?? null,
    },
  };
}

export function useRingConsumption({
  searchId,
  hasMoreRings,
  searchGeneration,
  queryKey,
  data,
  enabled = true,
}: UseRingConsumptionParams): RingConsumptionResult {
  const queryClient = useQueryClient();
  // Completion is tracked per search id so a new search re-arms consumption
  // without any imperative reset.
  const [finalSignalFor, setFinalSignalFor] = useState<string | null>(null);

  // Expansion fetches are serialized — one in-flight promise chain per search
  // — so out-of-order application cannot occur structurally. The version and
  // length guards in `mergeRingBatch` are a belt on top of that brace.
  const chainRef = useRef<{ searchId: string | null; tail: Promise<void> }>({
    searchId: null,
    tail: Promise.resolve(),
  });
  const refetchedRef = useRef<Set<string>>(new Set());
  // The last ring index seen from an Ably hint, per search. `k` on
  // `expanded_results` is ring-specific (server fetches `h3.grid_ring(center,
  // k)`, not the cumulative disk `0..k`), so this must be the exact ring a
  // hint named — there is no "highest ring" shortcut that covers earlier
  // rings too.
  const lastKRef = useRef<number>(1);
  // Whether an Ably hint has ever named a ring for this search. Only
  // `handleSearchUpdate` sets this true; the deadline/reconcile path below
  // never does, so a search that never receives a signal (Ably disabled,
  // unreachable, or the signal lost) falls through to `fallbackKRef` instead
  // of trusting a `lastKRef` that was never actually observed.
  const hasKnownRingRef = useRef<boolean>(false);
  // Fallback ring probe for when no Ably hint has ever arrived. Since the
  // server has no "which ring(s) still have undelivered content" signal,
  // each deadline/re-arm attempt probes the next unchecked ring in order
  // (1, then 2, then 3) rather than guessing a fixed k — content that landed
  // at ring 1 is found on the first attempt instead of being missed by a
  // fetch aimed at ring 3.
  const fallbackKRef = useRef<number>(1);

  // `applyBatch` must stay referentially stable across re-renders that don't
  // change anything it actually reads — its identity flows into the deadline
  // effect's dependency array below, and `queryKey` is reconstructed as a
  // new array/object on every render by the call sites (`restaurantKeys.search`/
  // `searchByLocation` spread a fresh `params` object each time). Depending on
  // `queryKey` directly would re-create `applyBatch` on every re-render,
  // which re-arms the deadline timer on every re-render too — a 5s polling
  // interval alone would make the 120s deadline (R2-M2) never fire. Held in a
  // ref instead: TanStack's `getQueryData`/`setQueryData` hash the key's
  // contents, not its identity, so a ref works fine here.
  const queryKeyRef = useRef(queryKey);
  useEffect(() => {
    queryKeyRef.current = queryKey;
  });

  // Read-then-write rather than a functional update: expansion fetches are
  // serialized and JS is single-threaded here, so nothing can interleave
  // between the two, and the merge decision stays observable for the
  // refuse-and-refetch-once rule below.
  const applyBatch = useCallback(
    (k: number, batch: ProgressiveSearchEnvelope): MergeDecision | null => {
      const current = queryClient.getQueryData<ProgressiveSearchEnvelope>(queryKeyRef.current);
      if (!current) return null;

      const decision = mergeRingBatch(current, batch, searchGeneration);
      if (decision.applied) {
        queryClient.setQueryData<ProgressiveSearchEnvelope>(queryKeyRef.current, decision.envelope);
      } else {
        console.warn(`[Search] Expansion batch k=${k} discarded (${decision.reason})`);
      }
      return decision;
    },
    [queryClient, searchGeneration],
  );

  // Fetches ring `hint.k`, merges what it returns, and resolves to the
  // envelope's own `expansionComplete` flag — the sole completion authority
  // (R2-M2). Resolves to `null` when nothing authoritative was learned: the
  // search already expired (terminal, and already resolved inline) or the
  // refuse-and-refetch-once budget ran out without a usable response. Never
  // reads `hint.is_final`/`hint.quota_exceeded` — those are advisory-only,
  // per the plan's explicit rule that every completion decision reads the
  // flag the task wrote.
  const fetchAndMerge = useCallback(
    async (hint: SearchUpdateHint): Promise<boolean | null> => {
      // A refused payload is dropped and re-fetched exactly once — a raced
      // write can leave the server a moment behind, and one retry resolves it
      // without turning a guard into a poll. A stale token is never retried:
      // the batch belongs to a different search and always will.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const expanded = await getExpandedSearchResults(hint.search_id, hint.k);

        if (expanded.error === 'search_expired') {
          console.warn('[Search] Search session expired, cannot fetch expanded results');
          setFinalSignalFor(hint.search_id);
          return null;
        }

        const decision = applyBatch(hint.k, expanded);
        if (!decision || decision.applied || decision.reason === 'stale_generation') {
          return expanded.expansionComplete;
        }

        const retryKey = `${hint.search_id}:${hint.k}`;
        if (refetchedRef.current.has(retryKey)) return expanded.expansionComplete;
        refetchedRef.current.add(retryKey);
      }
      return null;
    },
    [applyBatch],
  );

  const handleSearchUpdate = useCallback(
    (hint: SearchUpdateHint) => {
      lastKRef.current = hint.k;
      hasKnownRingRef.current = true;

      // A zero-new-results, non-terminal signal carries nothing actionable —
      // no batch to merge, and completion isn't being claimed — so it's
      // skipped without spending a fetch confirming nothing. A zero-count
      // signal that DOES claim completion (`is_final`/`quota_exceeded`)
      // still needs the authoritative fetch below; it just happens to carry
      // no new restaurants.
      if (hint.new_count === 0 && !hint.is_final && !hint.quota_exceeded) {
        return;
      }

      if (chainRef.current.searchId !== hint.search_id) {
        chainRef.current = { searchId: hint.search_id, tail: Promise.resolve() };
      }

      chainRef.current.tail = chainRef.current.tail.then(async () => {
        try {
          const expansionComplete = await fetchAndMerge(hint);
          // A failed or inconclusive fetch (caught below, or `null` here)
          // must never mark the search final — that would permanently
          // truncate results a retry, or the deadline/reconcile fallback,
          // could otherwise have recovered. Leaving `finalSignalFor` unset
          // lets that mechanism resolve it authoritatively instead.
          if (expansionComplete) {
            setFinalSignalFor(hint.search_id);
          }
        } catch (error) {
          console.error('[Search] Failed to fetch expanded results:', error);
        }
      });
    },
    [fetchAndMerge],
  );

  // D41c Phase 6 (R2-M2) — the deadline -> reconcile -> re-arm loop.
  // Completion is server-authoritative; this effect is only how the client
  // asks when it hasn't otherwise been told. Every decision below reads
  // `expansionComplete` off the reconciliation fetch — never the presence or
  // absence of an Ably signal, which this deliberately does not touch.
  useEffect(() => {
    if (!enabled || !searchId || !hasMoreRings || finalSignalFor === searchId) return;

    let cancelled = false;
    let rearmCount = 0;
    let timer: ReturnType<typeof setTimeout>;

    const scheduleNextOrResolve = () => {
      if (cancelled) return;
      if (rearmCount < MAX_DEADLINE_REARMS) {
        rearmCount += 1;
        timer = setTimeout(reconcile, EXPANSION_DEADLINE_MS);
      } else {
        // Bounded re-arms exhausted: a client that keeps waiting on a
        // signal that never arrives truncates the member's results just as
        // surely as one that spins forever — resolve to complete and log it
        // rather than do either.
        console.warn(
          `[Search] Expansion deadline exhausted after ${MAX_DEADLINE_REARMS} re-arms — resolving to complete`,
          { searchId },
        );
        setFinalSignalFor(searchId);
      }
    };

    const reconcile = async () => {
      // A ring named by an Ably hint is trusted as-is (D41c CR-8's original
      // path, unchanged). Otherwise probe the next ring this search hasn't
      // checked yet — never a fixed guess, since k is ring-specific and a
      // guess aimed at the wrong ring silently finds nothing.
      const k = hasKnownRingRef.current ? lastKRef.current : fallbackKRef.current;

      let expanded: ExpandedSearchResponse;
      try {
        expanded = await getExpandedSearchResults(searchId, k);
      } catch (error) {
        console.error('[Search] Expansion deadline reconciliation fetch failed:', error);
        scheduleNextOrResolve();
        return;
      }
      if (cancelled) return;

      if (expanded.error === 'search_expired') {
        setFinalSignalFor(searchId);
        return;
      }

      // Merge and decide BEHIND the Ably chain, never beside it. The fetch
      // above deliberately stays off the chain — parking the deadline behind
      // up to `SEARCH_REQUEST_TIMEOUT_MS` of pending work would defeat the
      // backstop it exists to be — but the WRITE and the completion decision
      // must both be serialized with every other write.
      //
      // Why (found by re-review, reproduced deterministically): `applyBatch`
      // is individually atomic (no `await` between its read and its write, so
      // no torn write is possible), but `mergeRingBatch`'s version and length
      // guards are MONOTONIC — they refuse anything that looks older than
      // what's already held. Two unserialized writers therefore turn those
      // guards from a safety net into a data-loss mechanism: if this
      // reconciliation's newer snapshot landed first, an Ably-driven batch
      // still in flight for an EARLIER ring would then be refused wholesale
      // (`stale_version`/`shrunken_order`) and its restaurants dropped. The
      // refuse-and-refetch-once path in `fetchAndMerge` usually recovers
      // that, but its budget is one retry per ring for the hook's whole life
      // — spend it on an ordinary raced-write refusal (which the `>=` version
      // rule above says to expect) and the loss is permanent, with the
      // subscription torn down and this effect disarmed behind it.
      //
      // Appending here makes merges land in the order they were scheduled,
      // which is the order the guards assume, and defers the completion
      // decision until the chain has drained.
      const settle = () => {
        if (cancelled) return;
        try {
          // By the time the chain drains, this payload may itself be older
          // than what the chain just wrote, in which case the merge is
          // refused — harmless, since it was only ever a bonus. The flag
          // read below is the point, and it stays valid either way:
          // `expansionComplete` is a property of the search and only ever
          // goes false -> true, so a slightly stale `true` is still correct
          // and a stale `false` merely re-arms.
          applyBatch(k, expanded);
          decideCompletion(k, expanded);
        } catch (error) {
          console.error('[Search] Expansion deadline reconciliation failed to settle:', error);
          scheduleNextOrResolve();
        }
      };

      // Only chain onto a tail that belongs to THIS search. When no Ably
      // signal has ever been handled for it the tail is unrelated (or a bare
      // resolved promise), and there is nothing to serialize against.
      if (chainRef.current.searchId === searchId) {
        // `settle` never rejects, so the tail stays resolvable — a rejected
        // tail would silently skip every `.then` appended after it.
        chainRef.current.tail = chainRef.current.tail.then(settle);
      } else {
        settle();
      }
    };

    const decideCompletion = (k: number, expanded: ExpandedSearchResponse) => {
      // `expansionComplete` is a property of the SEARCH (no more rings will
      // ever be discovered), never of the specific ring `k` this fetch just
      // probed. Found live: the blind fallback prober (no Ably hint ever
      // received) starts at k=1 and can see `expansionComplete: true` on
      // its very first attempt — the server can finish all 3 rings in well
      // under a second — while k=1 itself legitimately found nothing and
      // k=3's real batch was never fetched. Honoring the flag immediately
      // in that case stops the probe one or two rings short of the last
      // one, which is the same truncation this deadline loop exists to
      // prevent. While blindly probing, only treat `expansionComplete` as
      // a stop signal once the LAST ring (k=3) has actually been checked;
      // an Ably-hinted `k` (`hasKnownRingRef.current`) is trusted as the
      // search's real current ring, so the flag is honored immediately.
      if (expanded.expansionComplete && (hasKnownRingRef.current || k >= 3)) {
        setFinalSignalFor(searchId);
      } else {
        // Genuinely still running, or the fallback prober hasn't reached
        // the last ring yet — re-arm rather than truncate. Advance the
        // fallback probe to the next ring so the next attempt (if still
        // unhinted) doesn't re-check the same one.
        if (!hasKnownRingRef.current) {
          fallbackKRef.current = Math.min(fallbackKRef.current + 1, 3);
        }
        scheduleNextOrResolve();
      }
    };

    timer = setTimeout(reconcile, EXPANSION_DEADLINE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchId, enabled, hasMoreRings, finalSignalFor, applyBatch]);

  const stillExpanding = enabled && hasMoreRings && finalSignalFor !== searchId;

  const { isRealtimeActive } = useSearchUpdateSignal({
    searchId,
    onUpdate: handleSearchUpdate,
    enabled: !!searchId && stillExpanding,
  });

  return {
    restaurants: data?.restaurants ?? [],
    hasMoreRings: stillExpanding,
    isRealtimeActive,
    h3Status: data?.h3Status ?? null,
  };
}
